import { supabase } from './supabaseClient';

/**
 * AI Provider Admin Service
 * Manages AI provider configuration in Supabase
 */
class AIProviderAdminService {
  /**
   * Get all AI provider configurations
   */
  async getAllProviders() {
    try {
      const { data, error } = await supabase
        .from('ai_provider_config')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[AI Provider Admin] Error fetching providers:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get enabled providers only
   */
  async getEnabledProviders() {
    try {
      const { data, error } = await supabase
        .from('ai_provider_config')
        .select('*')
        .eq('enabled', true)
        .order('priority', { ascending: true });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[AI Provider Admin] Error fetching enabled providers:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get a specific provider by name
   */
  async getProvider(providerName) {
    try {
      const { data, error } = await supabase
        .from('ai_provider_config')
        .select('*')
        .eq('provider_name', providerName)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`[AI Provider Admin] Error fetching provider ${providerName}:`, error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Update provider configuration
   */
  async updateProvider(providerName, updates) {
    try {
      const { data, error } = await supabase
        .from('ai_provider_config')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('provider_name', providerName)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`[AI Provider Admin] Error updating provider ${providerName}:`, error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Toggle provider enabled status
   */
  async toggleProvider(providerName, enabled) {
    return this.updateProvider(providerName, { enabled });
  }

  /**
   * Update provider priority
   */
  async updatePriority(providerName, priority) {
    return this.updateProvider(providerName, { priority });
  }

  /**
   * Get usage logs with filters
   */
  async getUsageLogs(filters = {}) {
    try {
      let query = supabase
        .from('ai_usage_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.providerName) {
        query = query.eq('provider_name', filters.providerName);
      }
      if (filters.taskType) {
        query = query.eq('task_type', filters.taskType);
      }
      if (filters.success !== undefined) {
        query = query.eq('success', filters.success);
      }
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[AI Provider Admin] Error fetching usage logs:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get usage statistics
   */
  async getUsageStatistics(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('ai_usage_logs')
        .select('provider_name, task_type, success, total_tokens, estimated_cost')
        .gte('created_at', startDate.toISOString());

      if (error) throw error;

      // Calculate statistics
      const stats = {
        totalRequests: data.length,
        successfulRequests: data.filter(log => log.success).length,
        failedRequests: data.filter(log => !log.success).length,
        totalTokens: data.reduce((sum, log) => sum + (log.total_tokens || 0), 0),
        totalCost: data.reduce((sum, log) => sum + parseFloat(log.estimated_cost || 0), 0),
        byProvider: {},
        byTask: {}
      };

      // Group by provider
      data.forEach(log => {
        if (!stats.byProvider[log.provider_name]) {
          stats.byProvider[log.provider_name] = {
            requests: 0,
            successful: 0,
            failed: 0,
            totalTokens: 0,
            totalCost: 0
          };
        }
        const providerStats = stats.byProvider[log.provider_name];
        providerStats.requests++;
        if (log.success) providerStats.successful++;
        else providerStats.failed++;
        providerStats.totalTokens += log.total_tokens || 0;
        providerStats.totalCost += parseFloat(log.estimated_cost || 0);
      });

      // Group by task
      data.forEach(log => {
        if (!stats.byTask[log.task_type]) {
          stats.byTask[log.task_type] = {
            requests: 0,
            successful: 0,
            failed: 0,
            totalTokens: 0,
            totalCost: 0
          };
        }
        const taskStats = stats.byTask[log.task_type];
        taskStats.requests++;
        if (log.success) taskStats.successful++;
        else taskStats.failed++;
        taskStats.totalTokens += log.total_tokens || 0;
        taskStats.totalCost += parseFloat(log.estimated_cost || 0);
      });

      return { success: true, data: stats };
    } catch (error) {
      console.error('[AI Provider Admin] Error calculating statistics:', error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Log AI usage (called by AI services)
   */
  async logUsage(logData) {
    try {
      const { data, error } = await supabase
        .from('ai_usage_logs')
        .insert([{
          provider_name: logData.provider,
          model_name: logData.model,
          task_type: logData.task,
          success: logData.success !== false,
          duration_ms: logData.duration,
          prompt_tokens: logData.usage?.promptTokens || 0,
          completion_tokens: logData.usage?.completionTokens || 0,
          total_tokens: logData.usage?.totalTokens || 0,
          estimated_cost: logData.estimatedCost || 0,
          user_id: logData.userId || null,
          session_id: logData.sessionId || null,
          error_message: logData.error || null,
          metadata: logData.metadata || {}
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[AI Provider Admin] Error logging usage:', error);
      return { success: false, error: error.message, data: null };
    }
  }

  /**
   * Delete old usage logs (cleanup)
   */
  async cleanupOldLogs(daysToKeep = 90) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

      const { data, error } = await supabase
        .from('ai_usage_logs')
        .delete()
        .lt('created_at', cutoffDate.toISOString());

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[AI Provider Admin] Error cleaning up logs:', error);
      return { success: false, error: error.message, data: null };
    }
  }
}

// Export singleton instance
const aiProviderAdminService = new AIProviderAdminService();
export default aiProviderAdminService;
export { AIProviderAdminService };
